import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/api';
import { Upload, FileText, Plus, Loader2, Settings, ChevronRight, CheckCircle2, Clock, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STATUS_COLORS = {
  MANUAL: 'text-[#5C6A7A] bg-[#1E293B] border-[#2D3F5E]',
  SUCCESS: 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30',
  FAILED: 'text-red-400 bg-red-900/20 border-red-800/50',
  PENDING: 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30',
  NEEDS_REVIEW: 'text-orange-400 bg-orange-900/20 border-orange-800/50',
};
const PAY_COLORS = {
  unpaid: 'text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30',
  paid: 'text-[#57EB31] bg-[#57EB31]/10 border-[#57EB31]/30',
  overdue: 'text-red-400 bg-red-900/20 border-red-800/50',
  estimate_received: 'text-[#8B5CF6] bg-[#8B5CF6]/10 border-[#8B5CF6]/30',
  pending_approval: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/30',
};
const PAY_LABELS = {
  paid: 'Paid', unpaid: 'Unpaid', overdue: 'Overdue',
  estimate_received: 'Estimate', pending_approval: 'Pending',
};

export default function InvoiceTransformer() {
  const [tab, setTab] = useState('supplier');
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showTierModal, setShowTierModal] = useState(false);
  const [editTier, setEditTier] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [uploadForm, setUploadForm] = useState({ customer_id: '', truck_id: '', repair_id: '', vendor_name: '', invoice_number: '', invoice_date: '', notes: '' });
  const [uploadTrucks, setUploadTrucks] = useState([]);
  const [uploadRepairs, setUploadRepairs] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [tierForm, setTierForm] = useState({ name: '', description: '', markup_percent: 10, service_fee: 75, tax_rate: 0, is_default: false });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromRepairId = searchParams.get('from_repair');

  // Auto-prefill upload modal when user arrives from a repair's "Create Millennials Invoice" button
  useEffect(() => {
    if (!fromRepairId) return;
    (async () => {
      try {
        const r = await api.get(`/repairs/${fromRepairId}`);
        const rep = r.data;
        setUploadForm(prev => ({
          ...prev,
          customer_id: rep.customer_id || '',
          truck_id: rep.truck_id || '',
          repair_id: rep.id,
          vendor_name: rep.vendor_name || '',
        }));
        if (rep.customer_id) {
          api.get(`/customers/${rep.customer_id}/trucks`).then(tr => setUploadTrucks(tr.data || [])).catch(() => {});
          api.get('/repairs', { params: { customer_id: rep.customer_id, limit: 50 } }).then(rr => setUploadRepairs(rr.data?.items || [])).catch(() => {});
        }
        setShowUpload(true);
        toast.success(`Linked to repair: ${rep.title || rep.id.slice(0,8)}`);
      } catch (e) {
        toast.error('Could not load repair details');
      }
    })();
  }, [fromRepairId]);

  useEffect(() => { loadAll(); }, [tab]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sRes, cRes, tRes, custRes] = await Promise.allSettled([
        api.get('/invoice-transformer/supplier-invoices', { params: { limit: 50 } }),
        api.get('/invoice-transformer/customer-invoices', { params: { limit: 50 } }),
        api.get('/invoice-transformer/pricing-tiers'),
        api.get('/customers', { params: { limit: 100 } }),
      ]);
      if (sRes.status === 'fulfilled') setSupplierInvoices(sRes.value.data.items);
      if (cRes.status === 'fulfilled') setCustomerInvoices(cRes.value.data.items);
      if (tRes.status === 'fulfilled') setTiers(tRes.value.data);
      if (custRes.status === 'fulfilled') setCustomers(custRes.value.data.items || []);
      const failed = [sRes, cRes, tRes, custRes].filter(r => r.status === 'rejected');
      if (failed.length > 0) toast.warning(`${failed.length} resource(s) failed to load`);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return toast.error('Please select a PDF file');
    if (!uploadForm.customer_id) return toast.error('Please select a customer');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      Object.entries(uploadForm).forEach(([k, v]) => v && fd.append(k, v));
      const res = await api.post('/invoice-transformer/supplier-invoices', fd);
      toast.success('Supplier invoice uploaded!');
      setShowUpload(false);
      setSelectedFile(null);
      setUploadForm({ customer_id: '', truck_id: '', repair_id: '', vendor_name: '', invoice_number: '', invoice_date: '', notes: '' });
      setUploadTrucks([]);
      setUploadRepairs([]);
      navigate(`/ops/invoice-transformer/supplier/${res.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const loadUploadTrucks = async (cid) => {
    setUploadTrucks([]);
    setUploadRepairs([]);
    setUploadForm(p => ({ ...p, truck_id: '', repair_id: '' }));
    if (!cid) return;
    try {
      const r = await api.get('/trucks', { params: { customer_id: cid } });
      setUploadTrucks(r.data);
      // Also load repairs for this customer
      const rr = await api.get('/repairs', { params: { customer_id: cid, limit: 50 } });
      setUploadRepairs(rr.data.items.filter(r => !['completed','cancelled'].includes(r.status)));
    } catch {}
  };

  const saveTier = async (e) => {
    e.preventDefault();
    try {
      if (editTier) {
        await api.put(`/invoice-transformer/pricing-tiers/${editTier.id}`, tierForm);
        toast.success('Tier updated');
      } else {
        await api.post('/invoice-transformer/pricing-tiers', tierForm);
        toast.success('Tier created');
      }
      setShowTierModal(false);
      setEditTier(null);
      loadAll();
    } catch { toast.error('Failed to save tier'); }
  };

  const deleteTier = async (id) => {
    if (!window.confirm('Delete this pricing tier?')) return;
    await api.delete(`/invoice-transformer/pricing-tiers/${id}`);
    toast.success('Tier deleted');
    loadAll();
  };

  const deleteSupplierInvoice = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this supplier invoice? This cannot be undone.')) return;
    try {
      await api.delete(`/invoice-transformer/supplier-invoices/${id}`);
      toast.success('Supplier invoice deleted');
      setSupplierInvoices(prev => prev.filter(s => s.id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  return (
    <div className="p-6 space-y-5 animate-fade-in" data-testid="invoice-transformer-page">
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// OPS TOOL</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Invoice Generator</h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mt-0.5">Upload shop invoice → apply markup → generate branded customer invoice PDF</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => { setShowTierModal(true); setEditTier(null); setTierForm({ name: '', description: '', markup_percent: 10, service_fee: 75, tax_rate: 0, is_default: false }); }}
            className="flex items-center gap-2 px-3 py-2 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
            <Settings size={13} /> Pricing Tiers
          </button>
          <button onClick={() => setShowUpload(true)} data-testid="upload-supplier-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors">
            <Upload size={14} /> Upload Shop Invoice
          </button>
        </div>
      </div>

      {/* Pricing Tiers Summary */}
      {tiers.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {tiers.map(t => (
            <div key={t.id} onClick={() => { setEditTier(t); setTierForm(t); setShowTierModal(true); }}
              className={`card-industrial p-4 cursor-pointer hover:border-[#3B82F6]/40 transition-colors ${t.is_default ? 'border-[#3B82F6]/40' : ''}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white font-['Manrope'] font-bold text-sm">{t.name}</p>
                {t.is_default && <span className="text-[9px] font-['JetBrains_Mono'] text-[#3B82F6] uppercase tracking-wider">Default</span>}
              </div>
              <div className="flex gap-4">
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Markup</p>
                  <p className="text-[#3B82F6] font-['JetBrains_Mono'] font-bold text-lg">{t.markup_percent}%</p>
                </div>
                <div>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A]">Service Fee</p>
                  <p className="text-[#B8C0CC] font-['JetBrains_Mono'] font-bold">{fmt(t.service_fee)}</p>
                </div>
              </div>
            </div>
          ))}
          <button onClick={() => { setShowTierModal(true); setEditTier(null); setTierForm({ name: '', description: '', markup_percent: 10, service_fee: 75, tax_rate: 0, is_default: false }); }}
            className="card-industrial p-4 border-dashed flex items-center justify-center text-[#5C6A7A] hover:text-[#3B82F6] hover:border-[#3B82F6]/40 transition-colors">
            <Plus size={16} className="mr-2" /> Add Tier
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-[#2D3F5E]">
        {[['supplier', 'Shop Invoices', supplierInvoices.length], ['customer', 'Customer Invoices', customerInvoices.length]].map(([t, label, count]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest font-['JetBrains_Mono'] transition-colors border-b-2 -mb-px flex items-center gap-2 ${tab === t ? 'text-[#3B82F6] border-[#3B82F6]' : 'text-[#5C6A7A] border-transparent hover:text-[#B8C0CC]'}`}>
            {label}
            <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-[#1E293B] text-[#5C6A7A]">{count}</span>
          </button>
        ))}
      </div>

      {/* Supplier Invoices Table */}
      {tab === 'supplier' && (
        <div className="card-industrial overflow-hidden">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="text-[#3B82F6] animate-spin" size={24} /></div> : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D3F5E]">
                  {['','VENDOR','CUSTOMER','UNIT','INV #','INV DATE','EXTRACTED','ITEMS','STATUS',''].map((h,i) => (
                    <th key={i} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {supplierInvoices.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-16">
                    <Upload size={28} className="mx-auto mb-3 text-[#2D3F5E]" />
                    <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No shop invoices yet. Upload one to get started.</p>
                  </td></tr>
                ) : supplierInvoices.map((si, i) => (
                  <tr key={si.id} onClick={() => navigate(`/ops/invoice-transformer/supplier/${si.id}`)}
                    className="group border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] cursor-pointer transition-colors"
                    style={{ background: i%2!==0?'rgba(255,255,255,0.01)':'transparent' }}>
                    <td className="w-1 p-0" />
                    <td className="px-4 py-3 text-sm text-white font-['IBM_Plex_Sans'] font-medium">{si.supplier_vendor_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-[#B8C0CC] font-['IBM_Plex_Sans']">{si.customer_name}</td>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6] font-bold">{si.truck_unit_number || '—'}</td>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6]">{si.supplier_invoice_number || '—'}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{si.invoice_date || '—'}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">
                      {si.extraction_completed_at ? (
                        <span title={new Date(si.extraction_completed_at).toLocaleString()}>
                          {new Date(si.extraction_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          <span className="text-[#3D4A5C] ml-1">{new Date(si.extraction_completed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs font-['JetBrains_Mono'] text-[#B8C0CC]">{si.extracted_items?.length || 0} items</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${STATUS_COLORS[si.extraction_status] || STATUS_COLORS.MANUAL}`}>
                        {si.extraction_status}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1.5 justify-end">
                        <ChevronRight size={14} className="text-[var(--text-faint)]" />
                        <button
                          data-testid={`delete-supplier-invoice-${si.id}`}
                          onClick={(e) => deleteSupplierInvoice(e, si.id)}
                          className="p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-all hover:bg-red-900/30 hover:text-red-400 text-[var(--text-faint)]"
                          title="Delete invoice">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Customer Invoices Table */}
      {tab === 'customer' && (
        <div className="card-industrial overflow-hidden">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="text-[#3B82F6] animate-spin" size={24} /></div> : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D3F5E]">
                  {['INVOICE #','CUSTOMER','DATE','DUE DATE','TOTAL DUE','STATUS',''].map((h,i) => (
                    <th key={i} className="text-left px-4 py-3 text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customerInvoices.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-16">
                    <FileText size={28} className="mx-auto mb-3 text-[#2D3F5E]" />
                    <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No customer invoices generated yet.</p>
                  </td></tr>
                ) : customerInvoices.map((ci, i) => (
                  <tr key={ci.id} className="border-b border-[#2D3F5E]/30 hover:bg-[#2A1C14] transition-colors"
                    style={{ background: i%2!==0?'rgba(255,255,255,0.01)':'transparent' }}>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] text-xs text-[#3B82F6]">{ci.invoice_number}</td>
                    <td className="px-4 py-3 text-sm text-white font-['IBM_Plex_Sans']">{ci.customer_name}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{ci.invoice_date}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{ci.due_date}</td>
                    <td className="px-4 py-3 font-['JetBrains_Mono'] text-sm font-bold text-white">{fmt(ci.totals?.total_due)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide border font-['JetBrains_Mono'] ${PAY_COLORS[ci.payment_status] || PAY_COLORS.unpaid}`}>
                        {PAY_LABELS[ci.payment_status] || ci.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {ci.pdf_url && (
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${ci.pdf_url}?token=${localStorage.getItem('mm_token')}`}
                          target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                          className="px-2.5 py-1 text-[9px] font-bold uppercase text-[#57EB31] border border-[#57EB31]/40 rounded-sm hover:bg-[#57EB31]/10 transition-colors font-['JetBrains_Mono']">
                          PDF
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-lg">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Upload Shop Invoice</DialogTitle></DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-2">
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-2">PDF File *</label>
              <div className={`border-2 border-dashed rounded-sm p-4 text-center cursor-pointer transition-all ${uploadDragOver ? 'border-[#3B82F6] bg-[#3B82F6]/5' : 'border-[#2D3F5E] hover:border-[#3B82F6]/50'}`}
                onClick={() => document.getElementById('supplier-pdf-input').click()}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); setUploadDragOver(true); }}
                onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setUploadDragOver(true); }}
                onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setUploadDragOver(false); }}
                onDrop={e => {
                  e.preventDefault(); e.stopPropagation(); setUploadDragOver(false);
                  const file = e.dataTransfer?.files?.[0];
                  if (file && file.type === 'application/pdf') { setSelectedFile(file); }
                  else { toast.error('Please drop a PDF file'); }
                }}
                data-testid="upload-drop-zone">
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText size={16} className="text-[#57EB31]" />
                    <p className="text-[#57EB31] font-['IBM_Plex_Sans'] text-sm">{selectedFile.name}</p>
                    <button type="button" onClick={e => { e.stopPropagation(); setSelectedFile(null); }}
                      className="ml-2 text-[#5C6A7A] hover:text-red-400 transition-colors text-xs">✕</button>
                  </div>
                ) : (
                  <>
                    <Upload size={20} className={`mx-auto mb-2 ${uploadDragOver ? 'text-[#3B82F6]' : 'text-[#5C6A7A]'}`} />
                    <p className="text-xs text-[#B8C0CC]">Drop shop invoice PDF here</p>
                    <p className="text-[10px] text-[#5C6A7A] mt-1">or click to browse</p>
                  </>
                )}
                <input id="supplier-pdf-input" type="file" accept=".pdf" className="hidden"
                  onChange={e => setSelectedFile(e.target.files[0])} />
              </div>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">Customer *</label>
              <select value={uploadForm.customer_id}
                onChange={e => { setUploadForm(p => ({...p, customer_id: e.target.value, truck_id: '', repair_id: ''})); loadUploadTrucks(e.target.value); }}
                required
                className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]">
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>

            {/* Truck — only shows trucks for selected customer */}
            {uploadForm.customer_id && (
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">
                  Unit / Truck <span className="text-[#2D3F5E]">— which truck was serviced?</span>
                </label>
                <select value={uploadForm.truck_id}
                  onChange={e => setUploadForm(p => ({...p, truck_id: e.target.value}))}
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]"
                  data-testid="upload-truck-select">
                  <option value="">Select unit (optional)...</option>
                  {uploadTrucks.map(t => (
                    <option key={t.id} value={t.id}>{t.unit_number} — {t.year} {t.make} {t.model}</option>
                  ))}
                </select>
                {uploadTrucks.length === 0 && <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-1">No units found for this customer</p>}
              </div>
            )}

            {/* Repair — only shows repairs for selected customer */}
            {uploadForm.customer_id && (
              <div>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">
                  Link to Repair Event <span className="text-[#2D3F5E]">— connect to an existing repair?</span>
                </label>
                <select value={uploadForm.repair_id}
                  onChange={e => setUploadForm(p => ({...p, repair_id: e.target.value}))}
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]"
                  data-testid="upload-repair-select">
                  <option value="">No repair linked (optional)...</option>
                  {uploadRepairs.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.truck_unit_number || 'N/A'} — {(r.issue_description || '').slice(0, 50)} · {r.status?.replace(/_/g,' ')}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="border-t border-[#2D3F5E]/50 pt-3">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Shop Invoice Details</p>
              <div className="grid grid-cols-2 gap-3">
                {[['Vendor / Shop Name','vendor_name','text'],['Invoice #','invoice_number','text'],['Invoice Date','invoice_date','date']].map(([label,key,type]) => (
                  <div key={key} className={key === 'vendor_name' ? 'col-span-2' : ''}>
                    <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-1.5">{label}</label>
                    <input type={type} value={uploadForm[key]} onChange={e => setUploadForm(p => ({...p, [key]: e.target.value}))}
                      className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                  </div>
                ))}
              </div>
            </div>
            <button type="submit" disabled={uploading || !selectedFile || !uploadForm.customer_id} data-testid="confirm-upload-btn"
              className="w-full h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-2">
              {uploading ? <><Loader2 size={14} className="animate-spin" />Uploading...</> : <><Upload size={14} />Upload Invoice</>}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pricing Tier Modal */}
      <Dialog open={showTierModal} onOpenChange={setShowTierModal}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E]">
          <DialogHeader>
            <DialogTitle className="text-white font-['Manrope']">{editTier ? 'Edit Pricing Tier' : 'New Pricing Tier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTier} className="space-y-4 mt-2">
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A] mb-1.5">Tier Name *</label>
              <input type="text" required value={tierForm.name} onChange={e => setTierForm(p => ({...p, name: e.target.value}))}
                placeholder="e.g. Standard, Emergency Response..."
                className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A] mb-1.5">Description</label>
              <input type="text" value={tierForm.description} onChange={e => setTierForm(p => ({...p, description: e.target.value}))}
                className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[['Markup %','markup_percent','number'],['Service Fee ($)','service_fee','number'],['Tax Rate %','tax_rate','number']].map(([label,key,type]) => (
                <div key={key}>
                  <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widests text-[#5C6A7A] mb-1.5">{label}</label>
                  <input type={type} step="0.1" required value={tierForm[key]} onChange={e => setTierForm(p => ({...p, [key]: e.target.value}))}
                    className="w-full h-9 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['JetBrains_Mono'] text-sm focus:outline-none focus:border-[#3B82F6]" />
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={tierForm.is_default} onChange={e => setTierForm(p => ({...p, is_default: e.target.checked}))}
                className="w-4 h-4 accent-[#3B82F6]" />
              <span className="text-xs text-[#B8C0CC] font-['IBM_Plex_Sans']">Set as default tier</span>
            </label>
            <div className="flex gap-2">
              <button type="submit" className="flex-1 h-10 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wide rounded-sm hover:bg-[#2563EB]">
                {editTier ? 'Save Changes' : 'Create Tier'}
              </button>
              {editTier && (
                <button type="button" onClick={() => deleteTier(editTier.id)}
                  className="px-4 h-10 border border-red-800/50 text-red-400 rounded-sm text-xs hover:bg-red-900/20 transition-colors font-['JetBrains_Mono']">
                  Delete
                </button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
